(function () {
  'use strict';

  /*
  |--------------------------------------------------------------------------
  | GHC 3PL Shipping Check
  |--------------------------------------------------------------------------
  |
  | Checks the currently selected Shopify variant against our PHP endpoint.
  |
  | If variant is available ONLY at 3PL:
  |
  | properties[_3pl_fulfillment] = true
  |
  |--------------------------------------------------------------------------
  */

  const CONFIG = {

    endpoint:
      'https://shopifyapps.techievolve.com/te-3pl-shipping/check-3pl-inventory.php',

    productFormSelector:
      'form[data-type="add-to-cart-form"]',

    variantInputSelector:
      'input[name="id"]',

    propertyId:
      'te_3pl_fulfillment_property',

    propertyName:
      'properties[_3pl_fulfillment]',

    propertyValue:
      'true',

    debug:
      true
  };


  /*
  |--------------------------------------------------------------------------
  | Internal State
  |--------------------------------------------------------------------------
  */

  const variantCache = new Map();

  let activeController = null;

  let checkTimer = null;

  let requestSequence = 0;


  /*
  |--------------------------------------------------------------------------
  | Debug Logger
  |--------------------------------------------------------------------------
  */

  function log() {

    if (!CONFIG.debug) {
      return;
    }

    console.log(
      '[TE 3PL Shipping]',
      ...arguments
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Get Main Product Form
  |--------------------------------------------------------------------------
  */

  function getProductForm() {

    return document.querySelector(
      CONFIG.productFormSelector
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Get Current Variant ID
  |--------------------------------------------------------------------------
  */

  function getCurrentVariantId(
    productForm
  ) {

    if (!productForm) {
      return null;
    }

    const variantInput =
      productForm.querySelector(
        CONFIG.variantInputSelector
      );

    if (
      !variantInput ||
      !variantInput.value
    ) {

      return null;
    }

    return String(
      variantInput.value
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Find Existing 3PL Property
  |--------------------------------------------------------------------------
  */

  function get3PLPropertyInput(
    productForm
  ) {

    if (!productForm) {
      return null;
    }

    return productForm.querySelector(
      '#' + CONFIG.propertyId
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Remove 3PL Property
  |--------------------------------------------------------------------------
  */

  function remove3PLProperty(
    productForm
  ) {

    const existingInput =
      get3PLPropertyInput(
        productForm
      );

    if (existingInput) {

      existingInput.remove();

      log(
        'Removed _3pl_fulfillment property'
      );
    }
  }


  /*
  |--------------------------------------------------------------------------
  | Add 3PL Property
  |--------------------------------------------------------------------------
  */

  function add3PLProperty(
    productForm
  ) {

    if (!productForm) {
      return;
    }

    /*
     * Prevent duplicate inputs.
     */

    remove3PLProperty(
      productForm
    );


    const input =
      document.createElement(
        'input'
      );


    input.type =
      'hidden';


    input.id =
      CONFIG.propertyId;


    input.name =
      CONFIG.propertyName;


    input.value =
      CONFIG.propertyValue;


    productForm.appendChild(
      input
    );


    log(
      'Added property:',
      CONFIG.propertyName,
      '=',
      CONFIG.propertyValue
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Set Checking State
  |--------------------------------------------------------------------------
  */

  function setCheckingState(
    productForm,
    checking
  ) {

    if (!productForm) {
      return;
    }


    productForm.dataset
      .threePlChecking =
      checking
        ? 'true'
        : 'false';


    /*
    |--------------------------------------------------------------------------
    | Temporarily protect Add To Cart while the inventory check is running.
    |--------------------------------------------------------------------------
    */

    const addButton =
      productForm.querySelector(
        '.product-form__submit'
      );


    if (!addButton) {
      return;
    }


    if (checking) {

      if (
        !addButton.hasAttribute(
          'data-te-original-disabled'
        )
      ) {

        addButton.setAttribute(
          'data-te-original-disabled',
          addButton.disabled
            ? 'true'
            : 'false'
        );
      }


      addButton.disabled =
        true;

    } else {

      const originallyDisabled =
        addButton.getAttribute(
          'data-te-original-disabled'
        );


      if (
        originallyDisabled ===
        'false'
      ) {

        addButton.disabled =
          false;
      }


      addButton.removeAttribute(
        'data-te-original-disabled'
      );
    }
  }


  /*
  |--------------------------------------------------------------------------
  | Apply API Result
  |--------------------------------------------------------------------------
  */

  function applyResult(
    productForm,
    checkedVariantId,
    isThreePL
  ) {

    /*
     * The customer might have switched variants
     * while the AJAX request was running.
     */

    const currentVariantId =
      getCurrentVariantId(
        productForm
      );


    if (
      currentVariantId !==
      String(
        checkedVariantId
      )
    ) {

      log(
        'Variant changed before response. Ignoring old result.'
      );

      return;
    }


    if (isThreePL === true) {

      add3PLProperty(
        productForm
      );

    } else {

      remove3PLProperty(
        productForm
      );
    }


    productForm.dataset
      .threePlVariant =
      isThreePL
        ? 'true'
        : 'false';


    log(
      'Variant:',
      checkedVariantId,
      '3PL:',
      isThreePL
    );
  }


  /*
  |--------------------------------------------------------------------------
  | Check Current Variant
  |--------------------------------------------------------------------------
  */

  async function check3PLInventory() {

    const productForm =
      getProductForm();


    if (!productForm) {

      log(
        'Product form not found.'
      );

      return;
    }


    const variantId =
      getCurrentVariantId(
        productForm
      );


    if (!variantId) {

      log(
        'Variant ID not found.'
      );

      remove3PLProperty(
        productForm
      );

      return;
    }


    /*
    |--------------------------------------------------------------------------
    | Every variant change begins from a clean state.
    |--------------------------------------------------------------------------
    */

    remove3PLProperty(
      productForm
    );


    setCheckingState(
      productForm,
      true
    );


    log(
      'Checking variant:',
      variantId
    );


    /*
    |--------------------------------------------------------------------------
    | Cache
    |--------------------------------------------------------------------------
    |
    | If the customer returns to a variant we've already checked,
    | don't call PHP again.
    |
    |--------------------------------------------------------------------------
    */

    if (
      variantCache.has(
        variantId
      )
    ) {

      const cachedResult =
        variantCache.get(
          variantId
        );


      log(
        'Using cached result:',
        cachedResult
      );


      applyResult(
        productForm,
        variantId,
        cachedResult
      );


      setCheckingState(
        productForm,
        false
      );


      return;
    }


    /*
    |--------------------------------------------------------------------------
    | Cancel previous request
    |--------------------------------------------------------------------------
    */

    if (activeController) {

      activeController.abort();
    }


    activeController =
      new AbortController();


    const thisRequest =
      ++requestSequence;


    /*
    |--------------------------------------------------------------------------
    | Endpoint
    |--------------------------------------------------------------------------
    */

    const url =
      CONFIG.endpoint +
      '?variant_id=' +
      encodeURIComponent(
        variantId
      );


    log(
      'Request:',
      url
    );


    try {

      const response =
        await fetch(
          url,
          {

            method:
              'GET',

            headers: {
              'Accept':
                'application/json'
            },

            cache:
              'no-store',

            credentials:
              'omit',

            signal:
              activeController
                .signal
          }
        );


      if (!response.ok) {

        throw new Error(
          'HTTP ' +
          response.status
        );
      }


      const data =
        await response.json();


      log(
        'Response:',
        data
      );


      if (
        data.success !== true
        ||
        typeof data.is_3pl
          !== 'boolean'
      ) {

        throw new Error(
          'Invalid API response.'
        );
      }


      /*
      |--------------------------------------------------------------------------
      | Cache result
      |--------------------------------------------------------------------------
      */

      variantCache.set(
        variantId,
        data.is_3pl
      );


      /*
      |--------------------------------------------------------------------------
      | Ignore old request
      |--------------------------------------------------------------------------
      */

      if (
        thisRequest !==
        requestSequence
      ) {

        log(
          'Ignoring outdated request.'
        );

        return;
      }


      /*
      |--------------------------------------------------------------------------
      | Apply Result
      |--------------------------------------------------------------------------
      */

      applyResult(
        productForm,
        variantId,
        data.is_3pl
      );


    } catch (error) {


      if (
        error.name ===
        'AbortError'
      ) {

        log(
          'Previous request cancelled.'
        );

        return;
      }


      console.error(
        '[TE 3PL Shipping] Error:',
        error
      );


      /*
       * Fail safe:
       *
       * Don't leave a previous 3PL property
       * attached if API checking fails.
       */

      remove3PLProperty(
        productForm
      );


    } finally {


      /*
       * Only the newest request should unlock the button.
       */

      if (
        thisRequest ===
        requestSequence
      ) {

        setCheckingState(
          productForm,
          false
        );
      }
    }
  }


  /*
  |--------------------------------------------------------------------------
  | Debounced Variant Check
  |--------------------------------------------------------------------------
  */

  function schedule3PLCheck() {

    clearTimeout(
      checkTimer
    );


    checkTimer =
      setTimeout(
        function () {

          check3PLInventory();

        },
        150
      );
  }


  /*
  |--------------------------------------------------------------------------
  | Initialize
  |--------------------------------------------------------------------------
  */

  function initialize() {

    log(
      'Initialized'
    );

    check3PLInventory();
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );

  } else {

    initialize();
  }


  /*
  |--------------------------------------------------------------------------
  | Variant Radios
  |--------------------------------------------------------------------------
  |
  | Your theme already uses:
  |
  | variant-radios
  |
  |--------------------------------------------------------------------------
  */

  document.addEventListener(
    'click',
    function (event) {

      if (
        event.target.matches(
          'variant-radios input[type="radio"]'
        )
      ) {

        schedule3PLCheck();
      }
    }
  );


  /*
  |--------------------------------------------------------------------------
  | Variant Changes
  |--------------------------------------------------------------------------
  */

  document.addEventListener(
    'change',
    function (event) {

      if (

        event.target.matches(
          'variant-radios input'
        )

        ||

        event.target.matches(
          'variant-selects select'
        )

        ||

        event.target.matches(
          'select[name^="options"]'
        )

      ) {

        schedule3PLCheck();
      }
    }
  );


  /*
  |--------------------------------------------------------------------------
  | Manual Browser Console Test
  |--------------------------------------------------------------------------
  |
  | Run:
  |
  | teCheck3PL()
  |
  |--------------------------------------------------------------------------
  */

  window.teCheck3PL =
    check3PLInventory;

})();